import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSite } from "@/site/SiteContext";
import { scrollToSection } from "@/lib/scroll";

const HERO_BG =
  "https://images.unsplash.com/photo-1665322049384-e56e9ce06e68?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const Hero = () => {
  const { t, lang } = useLang();
  const site = useSite();
  const o = (site.hero && site.hero[lang]) || {};
  const badge = o.badge || t("hero.badge");
  const title = o.title || t("hero.title");
  const subtitle = o.subtitle || t("hero.subtitle");
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 180]);
  const contentY = useTransform(scrollY, [0, 600], [0, 120]);
  const contentOpacity = useTransform(scrollY, [0, 450], [1, 0]);

  const scrollTo = (id) => scrollToSection(id);

  return (
    <section id="home" className="relative min-h-screen flex items-center grain overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={HERO_BG} alt="" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/60 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-24"
      >
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-gold" />
            <span className="label-eyebrow">{badge}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-serif text-[2.75rem] sm:text-6xl lg:text-7xl leading-[1.05] text-white"
          >
            {title.split(",")[0]}
            {title.includes(",") && (
              <span className="block gold-gradient-text italic">
                {title.split(",").slice(1).join(",").trim()}
              </span>
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-7 text-base sm:text-lg text-white/70 font-sans leading-relaxed max-w-xl"
          >
            {subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-cta-quote"
              onClick={() => scrollTo("contact")}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold text-black font-sans font-semibold text-sm uppercase tracking-[0.15em] px-8 py-4 rounded-sm hover:bg-gold-deep transition-colors duration-300"
            >
              {t("hero.cta1")}
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-work"
              onClick={() => scrollTo("portfolio")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-sans font-semibold text-sm uppercase tracking-[0.15em] px-8 py-4 rounded-sm hover:bg-gold hover:text-black hover:border-gold transition-all duration-300"
            >
              {t("hero.cta2")}
            </button>
          </motion.div>
        </div>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2">
        <span className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-sans">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="h-8 w-px bg-gradient-to-b from-gold to-transparent"
        />
      </div>
    </section>
  );
};

export default Hero;
